import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from './AnimatedInspectorWrapper.module.css';

interface AnimatedWrapperProps {
  isVisible: boolean;
  onClose: () => void;
  addWithAnimation: boolean;
  closeWithAnimation: boolean;
  children: React.ReactNode;
  /** Override the close animation duration (ms). Defaults to the CSS value (250ms). */
  closeDurationMs?: number;
  /**
   * When true, the enter animation is suppressed on this component's initial mount.
   * Captured once at mount time — subsequent prop changes have no effect.
   * Useful for panels that should appear pre-opened without a slide-in.
   */
  skipInitialAnimation?: boolean;
}

const AnimatedInspectorWrapper: React.FC<AnimatedWrapperProps> = ({
  isVisible,
  onClose,
  addWithAnimation,
  closeWithAnimation,
  children,
  closeDurationMs,
  skipInitialAnimation = false,
}) => {
  const [shouldRender, setShouldRender] = useState(isVisible);
  const [isVisibleAnimationClass, setIsVisibleAnimationClass] = useState<string>(() => {
    if (!isVisible) return '';
    if (skipInitialAnimation) return styles.enterWithoutAnimation;
    return addWithAnimation ? styles.enter : styles.enterWithoutAnimation;
  });
  const [animationDuration, setAnimationDuration] = useState<string | undefined>(undefined);

  // Captured once at mount — subsequent prop changes don't affect it so the
  // decision is tied to the lifetime of this component instance.
  const capturedSkipRef = useRef(skipInitialAnimation);
  const prevIsVisibleRef = useRef(isVisible);
  const isFirstRunRef = useRef(true);

  useEffect(() => {
    const isFirst = isFirstRunRef.current;
    isFirstRunRef.current = false;
    const prevIsVisible = prevIsVisibleRef.current;
    prevIsVisibleRef.current = isVisible;

    if (isVisible) {
      setShouldRender(true);
      // Animate when: initial mount (unless caller requested skip), or false→true transition.
      // Never re-animate just because an unrelated dep changed while already visible.
      const shouldAnimate = isFirst
        ? !capturedSkipRef.current
        : prevIsVisible === false;
      if (shouldAnimate) {
        setIsVisibleAnimationClass(addWithAnimation ? styles.enter : styles.enterWithoutAnimation);
        setAnimationDuration(undefined);
      }
    } else {
      setIsVisibleAnimationClass(closeWithAnimation ? styles.exit : styles.exitWithoutAnimation);
      setAnimationDuration(closeDurationMs != null ? `${closeDurationMs}ms` : undefined);
    }
  }, [isVisible, addWithAnimation, closeWithAnimation, closeDurationMs]);

  const handleAnimationEnd = useCallback(() => {
    if (!isVisible) {
      setShouldRender(false);
      onClose();
    }
  }, [isVisible, onClose]);

  return (
    shouldRender && (
      <div
        className={isVisibleAnimationClass}
        onAnimationEnd={handleAnimationEnd}
        style={{height: '100%', pointerEvents: 'none', position: 'absolute', width: '100%', animationDuration}}
      >
        {children}
      </div>
    )
  );
};

export default React.memo(AnimatedInspectorWrapper);
